import {
  ClockIcon,
  FileDashedIcon,
  HourglassLowIcon,
  SealCheckIcon,
  UserCircleDashedIcon,
  WarningCircleIcon,
  type Icon
} from '@phosphor-icons/react';
import { complianceClass, COMPLIANCE_COLORS, type ComplianceStop } from 'src/features/reporting/utils/color';
import type { AppointmentResponse, EvidenceStatus, RecordStatus, SlotStatus } from './types';

/** Label copy for the backend's Training Record Expiring window. */
export const TRAINING_EXPIRING_WINDOW_LABEL = 'within 6 months';

/** Record Status values a Training Record can have, worst first. */
export const RECORD_STATUS_ORDER: readonly RecordStatus[] = [
  'expired',
  'pending',
  'unevidenced',
  'expiring',
  'evidenced'
];

/** Status icon; ``null`` renders as a neutral dot. */
export type Glyph = Icon | null;

/** Pill label, compliance stop, chart hex and icon for one status. */
export type StatusStyle = {
  label: string;
  stop: ComplianceStop;
  className: string;
  color: string;
  glyph: Glyph;
};

function statusStyle(label: string, stop: ComplianceStop, glyph: Glyph): StatusStyle {
  return { label, stop, className: complianceClass(stop), color: COMPLIANCE_COLORS[stop], glyph };
}

const RED_STOP: ComplianceStop = 1;
const ORANGE_STOP: ComplianceStop = 2;
const YELLOW_STOP: ComplianceStop = 3;
const LIGHT_BLUE_STOP: ComplianceStop = 4;
const BLUE_STOP: ComplianceStop = 5;
const NEUTRAL_STOP: ComplianceStop = 'na';

const EXPIRED = statusStyle('Expired', RED_STOP, WarningCircleIcon);
const EXPIRING = statusStyle('Expiring', LIGHT_BLUE_STOP, HourglassLowIcon);
/** One Evidence Document in date. */
const CURRENT = statusStyle('Current', BLUE_STOP, SealCheckIcon);
const PENDING = statusStyle('Pending', ORANGE_STOP, ClockIcon);
const UNEVIDENCED = statusStyle('Unevidenced', YELLOW_STOP, FileDashedIcon);
// A solid fill, as in the evidence waffle: the blue tint is too close to Expiring's sky tint to tell apart.
const EVIDENCED: StatusStyle = {
  ...statusStyle('Evidenced', BLUE_STOP, SealCheckIcon),
  className: 'bg-oi-blue-600 text-white'
};

const RECORD_STATUS: Record<RecordStatus, StatusStyle> = {
  expired: EXPIRED,
  pending: PENDING,
  expiring: { ...EXPIRING, label: `Expiring ${TRAINING_EXPIRING_WINDOW_LABEL}` },
  unevidenced: UNEVIDENCED,
  evidenced: EVIDENCED
};

export const SLOT_STATUS: Record<SlotStatus, StatusStyle> = {
  vacant: statusStyle('Vacant', RED_STOP, UserCircleDashedIcon),
  ...RECORD_STATUS,
  not_required: statusStyle('Not required', NEUTRAL_STOP, null)
};

export const EVIDENCE_STATUS: Record<EvidenceStatus, StatusStyle> = {
  expired: EXPIRED,
  pending: PENDING,
  expiring: EXPIRING,
  valid: CURRENT
};

/** Evidence Status values, worst first. */
export const EVIDENCE_STATUS_ORDER: readonly EvidenceStatus[] = ['expired', 'pending', 'expiring', 'valid'];

/** How many of ``statuses`` fall in each Evidence Status, worst first, leaving out statuses with none. */
export function countByEvidenceStatus(
  statuses: readonly EvidenceStatus[]
): { status: EvidenceStatus; count: number }[] {
  return EVIDENCE_STATUS_ORDER.map((status) => ({
    status,
    count: statuses.filter((candidate) => candidate === status).length
  })).filter(({ count }) => count > 0);
}

/** Revoked appointments appear in history only. */
const REVOKED_PILL: StatusStyle = statusStyle('Revoked', NEUTRAL_STOP, null);

/** Pill for one appointment: its Record Status, or Revoked when it has none. */
export function appointmentPill(appointment: Pick<AppointmentResponse, 'record_status'>): StatusStyle {
  return appointment.record_status == null ? REVOKED_PILL : RECORD_STATUS[appointment.record_status];
}

/** An appointments table row's status: a Vacancy, a Record Status, or Revoked for an Appointment with none. */
export type AppointmentRowStatus = 'vacant' | RecordStatus | 'revoked';

/** Appointments table row statuses, worst first. */
export const APPOINTMENT_ROW_STATUS_ORDER: readonly AppointmentRowStatus[] = [
  'vacant',
  ...RECORD_STATUS_ORDER,
  'revoked'
];

/** Row statuses of the active Appointments the tables list, worst first: none is ever Revoked. */
export const ACTIVE_APPOINTMENT_ROW_STATUS_ORDER = APPOINTMENT_ROW_STATUS_ORDER.filter(
  (status) => status !== 'revoked'
);

/** Pill for each row status, in one word so it fits the narrow Status column. */
export const APPOINTMENT_ROW_STATUS: Record<AppointmentRowStatus, StatusStyle> = {
  vacant: SLOT_STATUS.vacant,
  ...RECORD_STATUS,
  expiring: EXPIRING,
  revoked: REVOKED_PILL
};
